import React, { useEffect, useState } from 'react';
import { Text } from '@amzn/storm-ui';
import {
  HiddenContent,
  SideNavButtonInnerWrapper,
  SideNavLinkOuterWrapper,
  SideNavLinkTitleWrapper,
  SvgWrapper,
} from '../styles';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { ToolbarItem } from '@amzn/aac-chrome-internal-api';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { useIsRTL } from 'src/commons/hooks';
import { AllToolsAnchoredAlert } from './styles';
import { Flag } from 'src/commons/components/LaunchFlag/styles';
import { useDismissibleComponent } from '@amzn/storm-ui-utils';

interface SideNavAllToolsButtonProps {
  toolbarItem: ToolbarItem;
  onMouseEnter: (e) => void;
  onMouseLeave: (e) => void;
}

const ALL_TOOLS_ANCHORED_ALERT_ID = 'aac-all-tools-anchor';

const AllToolsIcon: React.VFC<{ toolbarItem: ToolbarItem }> = ({
  toolbarItem,
}) => {
  return (
    <SvgWrapper
      isSelected={false}
      data-ccx-e2e-id={toolbarItem.id}
      alignItems="center"
      justifyContent="center"
    >
      <SvgIcon
        iconPath={
          'https://m.media-amazon.com/images/G/01/CCX/svg/AllTools-light.svg'
        }
        ariaLabel={toolbarItem.toolTip}
        isSelected={false}
      />
    </SvgWrapper>
  );
};

const AllToolsHiddenContent: React.VFC<{
  toolTip: string;
  isAllToolsOpen: boolean;
}> = ({ toolTip, isAllToolsOpen }) => {
  return (
    <HiddenContent alignItems="center" justifyContent="space-between">
      <SideNavLinkTitleWrapper
        isAllToolsOpen={isAllToolsOpen}
        isFromAllTools={true}
      >
        {toolTip}
      </SideNavLinkTitleWrapper>
    </HiddenContent>
  );
};

const SideNavAllToolsButton: React.VFC<SideNavAllToolsButtonProps> = ({
  toolbarItem,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { isNavOpen, isAllToolsOpen, setIsAllToolsOpen } =
    useConsolidatedNavContext();
  const [isDismissed, dismissComponent] = useDismissibleComponent(
    ALL_TOOLS_ANCHORED_ALERT_ID,
  );
  const anchorPosition = useIsRTL() ? 'left' : 'right';
  const bundle = useSidenavBundle();
  const [portalElement, setPortalElement] = useState<Element | null>(null);
  useEffect(() => {
    const portal = document.getElementById('AACChromePortal');
    if (portal && document.body.contains(portal)) {
      setPortalElement(portal);
    }
    return () => {
      setPortalElement(null);
    };
  }, []);

  const portalRef = { current: portalElement as HTMLElement | null };

  const dynamicAllToolsAnchoredAlert = (
    <AllToolsAnchoredAlert
      key={`all-tools-anchored-alert-${isNavOpen}`}
      id={ALL_TOOLS_ANCHORED_ALERT_ID}
      position={anchorPosition}
      portalRef={portalRef}
      trigger={
        isNavOpen ? (
          <Flag variant="new" label="New" description="New Feature" />
        ) : (
          <p> </p>
        )
      }
      isOpen={!isDismissed}
      onCloseButtonClick={dismissComponent}
      variant="spotlight"
    >
      <Text renderAs="p">
        {bundle.getMessage('sidenav-all-tools-announcement')}
      </Text>
    </AllToolsAnchoredAlert>
  );

  const handleMouseEnter = (e) => {
    onMouseEnter(e);
    if (isNavOpen) {
      dismissComponent();
    }
  };

  return (
    <SideNavLinkOuterWrapper
      isSelected={false}
      onFocus={() => {
        if (!isAllToolsOpen) {
          setIsAllToolsOpen(true);
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <SideNavButtonInnerWrapper
        isNavOpen={isNavOpen}
        isSelected={false}
        data-ccx-e2e-id={toolbarItem.id}
        aria-label={toolbarItem.toolTip}
        {...getTaktAttributes(toolbarItem.id, chromeComponents.NAV, {
          url: '',
        })}
      >
        <AllToolsIcon toolbarItem={toolbarItem} />
        {isNavOpen ? (
          <>
            <AllToolsHiddenContent
              toolTip={toolbarItem.toolTip}
              isAllToolsOpen={isAllToolsOpen}
            />
            {dynamicAllToolsAnchoredAlert}
          </>
        ) : (
          <>
            {dynamicAllToolsAnchoredAlert}
            <AllToolsHiddenContent
              toolTip={toolbarItem.toolTip}
              isAllToolsOpen={isAllToolsOpen}
            />
          </>
        )}
      </SideNavButtonInnerWrapper>
    </SideNavLinkOuterWrapper>
  );
};

export default SideNavAllToolsButton;
